import { Button } from '@web-app/components/ui/Button/button';
import { Input } from '@web-app/components/ui/Input/input';
import { Textarea } from '@web-app/components/ui/Textarea/textarea';
import { FormEvent, useEffect, useRef, useState } from 'react';

import { isChecklistItemApplicable } from './checklist-visibility';
import {
  bmiEvidence,
  bmiSourceLabel,
  BRIDGE_DATE_OF_BIRTH_KEY,
  BRIDGE_INITIAL_BMI_EVIDENCE_KEY,
  BRIDGE_INITIAL_BMI_KEY,
  BRIDGE_THERAPY_COURSE_KEY,
  BRIDGE_THERAPY_START_KEY,
  BridgeClinicalPrefill,
  completedYears,
  type DobPresentation,
  dobPresentation,
  localToday,
  selectBmiEvidence,
  showTherapyStartInput,
  therapyStartSourceLabel,
} from './clinical-prefill';
import {
  bridgeIntakeStop,
  groupIntakeItems,
  itemsAfterIntakeStop,
  missingClinicalRequirements,
} from './intake-layout';
import {
  MedicareBridgeCaseView,
  MedicareBridgeChecklistItem,
  MedicareBridgeResponseValue,
  MedicareBridgeReviewDecision,
  SubmitMedicareBridgeReviewInput,
} from './medicare-bridge.types';
import {
  derivedReviewResponses,
  documentedTherapyStart,
  initialReviewResponses,
  newStartDate,
  reviewPromptItems,
} from './review-form-state';

const DECISIONS: { value: MedicareBridgeReviewDecision; label: string }[] = [
  { value: 'ELIGIBLE', label: 'Eligible' },
  { value: 'NOT_ELIGIBLE', label: 'Not eligible' },
  { value: 'NEEDS_INFORMATION', label: 'Needs information' },
];

const NUMBER_DRAFT = /^-?\d*\.?\d*$/;
const COMPLETE_NUMBER = /^-?(?:\d+(?:\.\d+)?|\.\d+)$/;

function parseChecklistNumber(
  value: MedicareBridgeResponseValue | undefined,
): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string' || !COMPLETE_NUMBER.test(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function submittedResponses(
  items: MedicareBridgeChecklistItem[],
  responses: Record<string, MedicareBridgeResponseValue>,
): Record<string, MedicareBridgeResponseValue> {
  const kindByKey = new Map(items.map((item) => [item.key, item.kind]));
  return Object.fromEntries(
    Object.entries(responses).flatMap(([key, value]) => {
      const submitted =
        kindByKey.get(key) === 'NUMBER' ? parseChecklistNumber(value) : value;
      if (submitted == null || submitted === '') return [];
      if (Array.isArray(submitted) && submitted.length === 0) return [];
      return [[key, submitted]];
    }),
  );
}

/** The platform owns checklist, route, and CMS rules. The form only requires a decision. */
export function clientReviewError(
  decision: MedicareBridgeReviewDecision | '',
): string | null {
  if (!decision) return 'Choose a Bridge decision.';
  return null;
}

export function MedicareBridgeReviewForm({
  bridgeCase,
  appointmentHealthieId,
  clinicalPrefill,
  prefillLoaded = false,
  prefillFailed = false,
  correcting = false,
  onSubmit,
  onCancel,
}: {
  bridgeCase: MedicareBridgeCaseView;
  appointmentHealthieId?: string;
  clinicalPrefill?: BridgeClinicalPrefill;
  /** True once Healthie age and measurement evidence has loaded. */
  prefillLoaded?: boolean;
  prefillFailed?: boolean;
  correcting?: boolean;
  onSubmit: (input: SubmitMedicareBridgeReviewInput) => Promise<void>;
  onCancel?: () => void;
}) {
  // Each submit replaces the latest review, so a continuation or correction
  // starts from that snapshot. Answers left untouched are sent again.
  // A yes/no item with no saved answer stays out of the snapshot; the
  // platform reports that as outstanding instead of "not met".
  const [decision, setDecision] = useState<MedicareBridgeReviewDecision | ''>(
    () => bridgeCase.latestReview?.decision ?? '',
  );
  const [nextRoute, setNextRoute] = useState(
    () => bridgeCase.latestReview?.nextRoute ?? '',
  );
  const [note, setNote] = useState(() => bridgeCase.latestReview?.note ?? '');
  const [unresolvedItems, setUnresolvedItems] = useState(() => {
    const saved = bridgeCase.latestReview?.unresolvedItems ?? [];
    const keys = new Set(bridgeCase.checklist.items.map((item) => item.key));
    return saved.filter((key) => keys.has(key));
  });
  const [responses, setResponses] = useState(() =>
    initialReviewResponses(bridgeCase),
  );
  const [bmiEdited, setBmiEdited] = useState(false);
  const [editingStart, setEditingStart] = useState(false);
  const appliedBmi = useRef<number | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() =>
    crypto.randomUUID(),
  );
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [today] = useState(() => localToday());
  const nextRoutes = bridgeCase.checklist.nextRoutes ?? [];
  const documentedDate = documentedTherapyStart(bridgeCase);
  const startDateForNewStart = newStartDate(bridgeCase, correcting, today);
  const savedBmi = bridgeCase.latestReview?.responses?.[BRIDGE_INITIAL_BMI_KEY];
  const savedDateOfBirth = bridgeCase.latestReview?.responses?.[BRIDGE_DATE_OF_BIRTH_KEY];
  const savedBmiEvidence =
    bridgeCase.latestReview?.responses?.[BRIDGE_INITIAL_BMI_EVIDENCE_KEY];
  const items = bridgeCase.checklist.items;
  const course = responses[BRIDGE_THERAPY_COURSE_KEY];
  const typedStart =
    typeof responses[BRIDGE_THERAPY_START_KEY] === 'string'
      ? responses[BRIDGE_THERAPY_START_KEY]
      : null;
  const startInputVisible =
    showTherapyStartInput(course, documentedDate, correcting) || editingStart;
  const startForBmi =
    course === 'NEW_START'
      ? startDateForNewStart
      : course === 'CONTINUING'
        ? startInputVisible
          ? typedStart
          : documentedDate
        : null;
  const bmiSelection = selectBmiEvidence({
    prefill: prefillLoaded ? clinicalPrefill : undefined,
    course,
    therapyStartDate: startForBmi,
  });
  const bmiFromPreviousReview =
    typeof savedBmi === 'number' &&
    responses[BRIDGE_INITIAL_BMI_KEY] === savedBmi &&
    !(bmiSelection.status === 'ready' && bmiSelection.evidence.bmi === savedBmi);
  const previousBmiEvidence =
    typeof savedBmiEvidence === 'string' ? savedBmiEvidence : null;
  const dobForAge = prefillLoaded
    ? (clinicalPrefill?.dateOfBirth ?? null)
    : prefillFailed && typeof savedDateOfBirth === 'string'
      ? savedDateOfBirth
      : null;
  const age = dobForAge ? completedYears(dobForAge, today) : null;
  const bmiItem = items.find((item) => item.key === BRIDGE_INITIAL_BMI_KEY);
  const bmiApplies =
    bmiItem != null && isChecklistItemApplicable(bmiItem, responses, items);
  const intakeStop = bridgeIntakeStop({
    hasDateOfBirth: items.some((item) => item.key === BRIDGE_DATE_OF_BIRTH_KEY),
    age,
    treatmentPurpose: responses['treatment_purpose'],
    alternate: responses['alternate_part_d_indication'],
    bmi: bmiApplies
      ? parseChecklistNumber(responses[BRIDGE_INITIAL_BMI_KEY])
      : null,
  });
  const activeItems = itemsAfterIntakeStop(items, intakeStop);
  const visibleItems = activeItems.filter(
    (item) =>
      item.key !== BRIDGE_INITIAL_BMI_EVIDENCE_KEY &&
      isChecklistItemApplicable(item, responses, activeItems),
  );
  const intake = groupIntakeItems(visibleItems);
  const promptItems = reviewPromptItems(
    activeItems,
    responses,
    documentedDate,
    correcting,
  );
  const dobView = dobPresentation({
    loaded: prefillLoaded,
    failed: prefillFailed,
    dateOfBirth: clinicalPrefill?.dateOfBirth,
    savedDateOfBirth:
      typeof savedDateOfBirth === 'string' ? savedDateOfBirth : null,
    today,
    age,
  });
  const sections = [
    { title: 'Treatment screening', items: intake.screening },
    { title: 'BMI and qualifying conditions', items: intake.bmi },
    { title: 'Treatment plan', items: intake.plan },
  ].filter((section) => section.items.length > 0);

  useEffect(
    function applyHealthieBmi() {
      if (!prefillLoaded || bmiEdited) return;
      const evidence =
        bmiSelection.status === 'ready' ? bmiSelection.evidence : null;
      setResponses((current) => {
        const existing = current[BRIDGE_INITIAL_BMI_KEY];
        const ownsValue =
          existing == null ||
          existing === '' ||
          existing === appliedBmi.current;
        if (!ownsValue) return current;
        if (!evidence) {
          if (existing == null || existing === '') return current;
          appliedBmi.current = null;
          const next = { ...current };
          delete next[BRIDGE_INITIAL_BMI_KEY];
          return next;
        }
        if (existing === evidence.bmi) {
          appliedBmi.current = evidence.bmi;
          return current;
        }
        appliedBmi.current = evidence.bmi;
        return { ...current, [BRIDGE_INITIAL_BMI_KEY]: evidence.bmi };
      });
    },
    [bmiEdited, bmiSelection, prefillLoaded],
  );

  function selectDecision(next: MedicareBridgeReviewDecision) {
    setDecision(next);
    setError(null);
    if (next === 'ELIGIBLE') setNextRoute('');
  }

  function toggleUnresolved(key: string, checked: boolean) {
    setUnresolvedItems((current) =>
      checked ? [...current, key] : current.filter((item) => item !== key),
    );
  }

  function setResponse(key: string, value: MedicareBridgeResponseValue) {
    if (key === BRIDGE_INITIAL_BMI_KEY) setBmiEdited(true);
    if (key === BRIDGE_THERAPY_COURSE_KEY) setEditingStart(false);
    setResponses((current) => ({ ...current, [key]: value }));
  }

  function beginStartEdit() {
    setEditingStart(true);
    setResponses((current) => {
      if (current[BRIDGE_THERAPY_START_KEY] || !documentedDate) return current;
      return { ...current, [BRIDGE_THERAPY_START_KEY]: documentedDate };
    });
  }

  function currentDerived() {
    return derivedReviewResponses({
      items: activeItems,
      responses,
      documentedDate,
      correcting,
      dateOfBirth: prefillLoaded
        ? (clinicalPrefill?.dateOfBirth ?? null)
        : undefined,
      bmiEvidence: bmiEvidence({
        selection: bmiSelection,
        edited: bmiEdited,
        fromPreviousReview: bmiFromPreviousReview,
        savedEvidence: previousBmiEvidence,
      }),
      newStartDate: startDateForNewStart,
      editingTherapyStart: editingStart,
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const clientError = clientReviewError(decision);
    if (clientError || !decision) {
      setError(clientError);
      return;
    }

    const trimmedNote = note.trim();
    const derived = currentDerived();
    const promptKeys = new Set(promptItems.map((item) => item.key));
    const payload: SubmitMedicareBridgeReviewInput = {
      caseId: bridgeCase.id,
      checklistVersion: bridgeCase.checklist.version,
      responses: submittedResponses(items, derived),
      decision,
      idempotencyKey,
      note: trimmedNote || undefined,
    };
    if (appointmentHealthieId) {
      payload.appointmentHealthieId = appointmentHealthieId;
    }
    const trimmedRoute = nextRoute.trim();
    if (decision !== 'ELIGIBLE' && trimmedRoute) {
      payload.nextRoute = trimmedRoute;
    }
    if (decision === 'NEEDS_INFORMATION') {
      const stillOpen = unresolvedItems.filter((key) => promptKeys.has(key));
      if (stillOpen.length > 0) payload.unresolvedItems = stillOpen;
    }

    setPending(true);
    setError(null);
    try {
      await onSubmit(payload);
      setIdempotencyKey(crypto.randomUUID());
    } catch (submitError) {
      setError(
        submitError instanceof Error && submitError.message
          ? submitError.message
          : 'Could not save the Bridge review. Try again.',
      );
    } finally {
      setPending(false);
    }
  }

  function checklistRow(item: MedicareBridgeChecklistItem) {
    const canEditStart =
      item.key === BRIDGE_THERAPY_START_KEY &&
      course === 'CONTINUING' &&
      Boolean(documentedDate) &&
      !correcting &&
      !editingStart;
    return (
      <ChecklistRow
        key={item.key}
        item={item}
        value={responses[item.key]}
        onChange={(value) => setResponse(item.key, value)}
        dob={item.key === BRIDGE_DATE_OF_BIRTH_KEY ? dobView : null}
        therapyLabel={
          item.key === BRIDGE_THERAPY_START_KEY
            ? therapyStartSourceLabel({
                course,
                documentedDate,
                correcting,
                newStartDate: startDateForNewStart,
              })
            : null
        }
        showTherapyInput={
          item.key !== BRIDGE_THERAPY_START_KEY ||
          showTherapyStartInput(course, documentedDate, correcting) ||
          editingStart
        }
        onEditStart={canEditStart ? beginStartEdit : undefined}
        bmiSource={
          item.key === BRIDGE_INITIAL_BMI_KEY
            ? bmiSourceLabel(
                bmiSelection,
                bmiEdited,
                bmiFromPreviousReview,
                previousBmiEvidence,
              )
            : null
        }
      />
    );
  }

  const missingRequirements = intake.preview
    ? missingClinicalRequirements(visibleItems, currentDerived())
    : [];

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
      <fieldset className="flex flex-col gap-2">
        {intake.preview ? null : (
          <legend className="text-sm font-semibold text-foreground">
            Checklist
          </legend>
        )}
        <div data-testid="medicare-bridge-checklist-items" className="flex flex-col gap-4">
          {intake.preview ? (
            <>
              {visibleItems
                .filter((item) => item.key === BRIDGE_DATE_OF_BIRTH_KEY)
                .map(checklistRow)}
              {sections.map((section, index) => (
                <fieldset
                  key={section.title}
                  className="flex flex-col gap-3 border-t border-border pt-3"
                >
                  <legend className="text-sm font-semibold text-foreground">
                    {index + 1}. {section.title}
                  </legend>
                  {section.items.map(checklistRow)}
                </fieldset>
              ))}
              {intakeStop ? (
                <p
                  className="text-sm text-foreground"
                  data-testid="medicare-bridge-intake-stop"
                >
                  {intakeStop.message}
                </p>
              ) : null}
              {intake.other.map(checklistRow)}
            </>
          ) : (
            visibleItems.map(checklistRow)
          )}
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-semibold text-foreground">
          Decision
        </legend>
        {DECISIONS.map((option) => (
          <label key={option.value} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="medicare-bridge-decision"
              value={option.value}
              checked={decision === option.value}
              onChange={() => selectDecision(option.value)}
            />
            {option.label}
          </label>
        ))}
      </fieldset>

      {decision !== '' && decision !== 'ELIGIBLE' && nextRoutes.length > 0 && (
        <label className="flex flex-col gap-1 text-sm text-foreground">
          Next route
          <select
            className="rounded-md border border-border bg-background px-2 py-1"
            value={nextRoute}
            onChange={(event) => setNextRoute(event.target.value)}
          >
            <option value="">Select</option>
            {nextRoutes.map((route) => (
              <option key={route.value} value={route.value}>
                {route.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {decision === 'NEEDS_INFORMATION' && (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-semibold text-foreground">
            Still unresolved
          </legend>
          {promptItems.map((item) => (
            <label key={item.key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={unresolvedItems.includes(item.key)}
                onChange={(event) =>
                  toggleUnresolved(item.key, event.target.checked)
                }
              />
              {item.label}
            </label>
          ))}
        </fieldset>
      )}

      <label className="flex flex-col gap-1 text-sm text-foreground">
        Note
        <Textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </label>

      {intake.preview && !intakeStop ? (
        <p
          className={
            missingRequirements.length === 0
              ? 'rounded-md bg-muted px-3 py-2 text-center text-sm text-muted-foreground'
              : 'rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground'
          }
          data-testid="medicare-bridge-clinical-requirements"
        >
          {missingRequirements.length === 0
            ? 'Clinical requirements complete'
            : `Still needed: ${missingRequirements.join(', ')}`}
        </p>
      ) : null}

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={pending}>
          Save Bridge review
        </Button>
        {onCancel ? (
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={onCancel}
          >
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
}

function ChecklistRow({
  item,
  value,
  onChange,
  dob,
  therapyLabel,
  showTherapyInput,
  onEditStart,
  bmiSource,
}: {
  item: MedicareBridgeChecklistItem;
  value: MedicareBridgeResponseValue | undefined;
  onChange: (value: MedicareBridgeResponseValue) => void;
  dob: DobPresentation | null;
  therapyLabel: string | null;
  showTherapyInput: boolean;
  onEditStart?: () => void;
  bmiSource: string | null;
}) {
  if (item.key === BRIDGE_DATE_OF_BIRTH_KEY) {
    if (dob?.kind === 'age') {
      return (
        <p className="text-sm text-foreground" data-testid="medicare-bridge-dob">
          <span className="font-medium">Age {dob.age}</span>{' '}
          <span className="text-muted-foreground">From Healthie</span>
        </p>
      );
    }
    return (
      <p className="text-sm text-foreground" data-testid="medicare-bridge-dob">
        {dob?.kind === 'message' ? dob.text : null}
      </p>
    );
  }
  if (item.key === BRIDGE_THERAPY_START_KEY && !showTherapyInput) {
    return therapyLabel ? (
      <div className="flex items-center gap-2 text-sm text-foreground">
        <span>{therapyLabel}</span>
        {onEditStart ? (
          <Button
            type="button"
            variant="link"
            size="xs"
            className="h-auto px-0"
            onClick={onEditStart}
          >
            Edit start date
          </Button>
        ) : null}
      </div>
    ) : null;
  }

  return (
    <div className="flex flex-col gap-1">
      <ChecklistControl item={item} value={value} onChange={onChange} />
      {bmiSource ? (
        <p className="text-xs text-muted-foreground">{bmiSource}</p>
      ) : null}
    </div>
  );
}

function ChecklistControl({
  item,
  value,
  onChange,
}: {
  item: MedicareBridgeChecklistItem;
  value: MedicareBridgeResponseValue | undefined;
  onChange: (value: MedicareBridgeResponseValue) => void;
}) {
  return (
    <div className="flex flex-col gap-1 text-sm text-foreground">
      {item.kind === 'CHOICE' ? (
        <label className="flex flex-col gap-1">
          {itemLabel(item)}
          <select
            className="rounded-md border border-border bg-background px-2 py-1"
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => onChange(event.target.value)}
          >
            <option value="">Select</option>
            {(item.options ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      ) : item.kind === 'CHOICES' ? (
        <fieldset className="flex flex-col gap-1">
          <legend>{itemLabel(item)}</legend>
          {(item.options ?? []).map((option) => {
            const selected = Array.isArray(value) && value.includes(option.value);
            return (
              <label key={option.value} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={(event) =>
                    onChange(
                      toggleChoice(
                        Array.isArray(value) ? value : [],
                        option.value,
                        event.target.checked,
                      ),
                    )
                  }
                />
                {option.label}
              </label>
            );
          })}
        </fieldset>
      ) : item.kind === 'NUMBER' || item.kind === 'DATE' || item.kind === 'EVIDENCE' ? (
        <label className="flex flex-col gap-1">
          {itemLabel(item)}
          <Input
            type={item.kind === 'DATE' ? 'date' : 'text'}
            inputMode={item.kind === 'NUMBER' ? 'decimal' : undefined}
            value={
              typeof value === 'number'
                ? String(value)
                : typeof value === 'string'
                  ? value
                  : ''
            }
            onChange={(event) => {
              const next = event.target.value;
              if (
                item.kind === 'NUMBER' &&
                next !== '' &&
                !NUMBER_DRAFT.test(next)
              ) {
                return;
              }
              onChange(next);
            }}
          />
        </label>
      ) : (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(event) => onChange(event.target.checked)}
          />
          {itemLabel(item)}
        </label>
      )}
    </div>
  );
}

function itemLabel(item: MedicareBridgeChecklistItem): string {
  return item.required ? `${item.label} (required)` : item.label;
}

function toggleChoice(
  current: string[],
  option: string,
  checked: boolean,
): string[] {
  if (checked) {
    return current.includes(option) ? current : [...current, option];
  }
  return current.filter((value) => value !== option);
}
